import type { InboxFacet } from '@chat-emp/shared';

import type { GroupBy, InboxItem } from '../fixtures/domain.js';
import type { JobLeaf, JobTreeNode } from './job-tree.js';

/**
 * The funnel for a saved list, done client-side over its loaded members — the
 * mock's shape (InboxService.filter/group), not the inbox's.
 *
 * The inbox's own funnel is server-side and gated to the Job view. A list is a
 * hand-picked set already held client-side (see InboxPanel's `scopedList`), so
 * filtering, grouping and the options themselves all come from the members here,
 * with no read-model round trip. Deriving the options from the members means the
 * funnel only ever offers a job or a place that the list actually holds.
 *
 * One limit vs the Job view: a loaded item carries no parent job (that lives
 * only in the server facets), so the job filter here is flat — one row per job,
 * no parent/sub-role tree.
 */

const byText = (a: string, b: string): number => String(a).localeCompare(String(b));

/** The job filter's options, from the members. Flat: no parent tree (see above). */
export function listJobTree(items: InboxItem[]): JobTreeNode[] {
  const jobs = new Map<string, JobLeaf>();
  for (const { channel } of items) {
    if (!channel.jobMeta) continue;
    const { jobId, jobTitle, jobLocation } = channel.jobMeta;
    const entry = jobs.get(jobId);
    if (entry) entry.count += 1;
    else
      jobs.set(jobId, {
        id: jobId,
        title: jobTitle,
        ...(jobLocation ? { location: jobLocation } : {}),
        count: 1,
        selectable: true,
      });
  }
  return [...jobs.values()]
    .map((parent) => ({ parent, children: [], familyCount: parent.count, kind: 'job' as const }))
    .sort((a, b) => byText(a.parent.title, b.parent.title));
}

/**
 * The location chips' options, from the members, narrowed by the picked jobs —
 * jobs narrow locations, never the reverse, as the inbox funnel does. A member
 * whose job has no location contributes no chip.
 */
export function listLocations(items: InboxItem[], jobIds: string[]): InboxFacet[] {
  const places = new Map<string, number>();
  for (const { channel } of items) {
    const job = channel.jobMeta;
    if (!job?.jobLocation) continue;
    if (jobIds.length > 0 && !jobIds.includes(job.jobId)) continue;
    places.set(job.jobLocation, (places.get(job.jobLocation) ?? 0) + 1);
  }
  return [...places.entries()]
    .map(([key, count]) => ({ key, label: key, count }))
    .sort((a, b) => byText(a.key, b.key));
}

/**
 * Keep members matching the picked jobs AND the picked locations — an OR within
 * each set, AND between them. Empty picks pass everything unchanged.
 */
export function filterListItems(
  items: InboxItem[],
  jobIds: string[],
  locations: string[],
): InboxItem[] {
  if (jobIds.length === 0 && locations.length === 0) return items;
  return items.filter(({ channel }) => {
    const job = channel.jobMeta;
    if (jobIds.length > 0 && !(job && jobIds.includes(job.jobId))) return false;
    if (locations.length > 0 && !(job?.jobLocation && locations.includes(job.jobLocation)))
      return false;
    return true;
  });
}

/** A client-side group of a list's members. */
export interface ListGroup {
  key: string;
  label: string;
  items: InboxItem[];
}

/** Members with no job, grouped by job, fall here — matching the mock. */
const DIRECT_KEY = '__direct';

/**
 * Group the members client-side. Only `job` and `person` are user-selectable
 * (FilterPanel's GROUPS); any other value is one undivided group. Insertion
 * order is kept — members arrive newest-first from the read model, and so do
 * the groups (by first appearance).
 */
export function groupListItems(items: InboxItem[], groupBy: GroupBy): ListGroup[] {
  if (groupBy !== 'job' && groupBy !== 'person') {
    return [{ key: 'all', label: '', items }];
  }
  const groups = new Map<string, ListGroup>();
  for (const item of items) {
    const [key, label] =
      groupBy === 'job'
        ? [
            item.channel.jobMeta?.jobId ?? DIRECT_KEY,
            item.channel.jobMeta?.jobTitle ?? 'Direct messages',
          ]
        : [item.counterpart.id, item.counterpart.name];
    const group = groups.get(key);
    if (group) group.items.push(item);
    else groups.set(key, { key, label, items: [item] });
  }
  return [...groups.values()];
}

/**
 * The Unread pill's rows in a list detail, holding what the employer just read.
 *
 * `rowFate`'s `settled`, for a list that has no cursor to hold. The detail's
 * members refresh live now, so a read really does take a member out of the cut —
 * and cutting on the spot deletes the row under the click: the conversation
 * opens in the pane while the list beside it goes empty. The inbox answered this
 * by holding a row until the cut is asked again; a list's cut is asked when the
 * pill is pressed or the funnel moves, which is what resets `held`.
 *
 * `held` is the caller's, and is added to in place — it belongs to one asking of
 * the cut and is thrown away with it, so a caller that keeps it too long shows
 * rows it should have dropped and one that resets it too eagerly yanks a row
 * mid-click.
 *
 * Rows only. `listCounts` still counts live, so the number falls the moment the
 * chat is read: the same one-directional trade the inbox makes, and for the same
 * reason — a count is a claim about now, a list is what you were given to work
 * through.
 */
export function heldUnreadCut(items: InboxItem[], unread: boolean, held: Set<string>): InboxItem[] {
  if (!unread) return items;
  return items.filter((item) => {
    // Idempotent, so re-running it in a double render costs nothing and the set
    // only ever grows within one asking of the cut.
    if (item.channel.unreadCount > 0) {
      held.add(item.channel.id);
      return true;
    }
    return held.has(item.channel.id);
  });
}

/**
 * The numbers on a saved list's scope pills.
 *
 * Both are read off the funnelled members, **before** the Unread pill's cut:
 * "All" means everything the funnel left, not everything the pill is currently
 * showing. Counting the rendered set instead is the silent way to break this —
 * pressing Unread would shrink "All" to match it, and both pills would agree
 * on a number that is only true of one of them.
 *
 * A length is honest here in a way it would not be for the inbox: a list's
 * members are fully loaded, not a page of a keyset query.
 */
export function listCounts(funnelled: InboxItem[]): { all: number; unread: number } {
  return {
    all: funnelled.length,
    // A list counts its own members rather than asking the read model — its
    // membership is hand-picked and fully loaded, so there is nothing to page.
    // The cut is still the read model's, though: `unreadCount` mirrors the
    // `scope: 'unread'` clause the inbox sends (see `mapChannel`), so a list's
    // pill and the inbox's count the same thing.
    unread: funnelled.filter((item) => item.channel.unreadCount > 0).length,
  };
}
